/**
 * API端点配置 (V2 API)
 * 集中管理所有API端点，已迁移到统一的V2 API
 */

const endpoints = {
  // 探索相关 - 迁移到统一内容API
  explorations: {
    list: '/contents',  // 通过查询参数筛选
    detail: (id) => `/contents/${id}`,
    search: '/contents/search',
  },

  // 任务相关 - 迁移到统一内容API
  tasks: {
    list: '/contents',  // ?type=article&category=task
    detail: (id) => `/contents/${id}`,
    search: '/contents/search',
    create: '/contents',
    update: (id) => `/contents/${id}`,
    delete: (id) => `/contents/${id}`,
  },

  // 资产相关 - 迁移到统一内容API
  assets: {
    list: '/contents',  // ?content_type=certificate 或 content_type=ai_agent
    detail: (id) => `/contents/${id}`,
    search: '/contents/search',
    stats: '/contents/search', // 统计通过search接口实现
  },

  // 用户相关
  users: {
    current: '/users/current',
    list: '/users',
    detail: (id) => `/users/${id}`,
    search: '/users/search',
    // 用户个人相关
    me: {
      teams: (id) => `/users/${id}/groups`,
      invitations: '/users/me/invitations',
      applications: '/users/me/applications',
    },
    // 社交链接相关
    socialLinks: {
      create: (userId) => `/users/${userId}/social-links`,
      list: (userId) => `/users/${userId}/social-links`,
      update: (userId, type) => `/users/${userId}/social-links/${type}`,
      delete: (userId, type) => `/users/${userId}/social-links/${type}`,
      batchUpdate: (userId) => `/users/${userId}/social-links`, // 批量更新（PUT方法）
    },
  },

  // 团队相关 (API规范: /v1/syntrust/teams)
  teams: {
    list: '/groups',
    detail: (id) => `/groups/${id}`,
    create: `/groups`,
    update: (id) => `/groups/${id}`,
    delete: (id) => `/groups/${id}`,
    // 团队成员管理
    members: {
      list: (teamId) => `/groups/${teamId}/members`,
      remove: (teamId, userId) => `/groups/${teamId}/members/${userId}`,
      update: (teamId, userId) => `/groups/${teamId}/members/${userId}`,
    },
    // 团队邀请
    invitations: {
      create: (teamId) => `/groups/${teamId}/invitations`,
      list: (teamId) => `/groups/${teamId}/invitations`,
      delete: (teamId, invitationId) => `/groups/${teamId}/invitations/${invitationId}`,
    },
    // 入团申请
    applications: {
      join: (teamId) => `/groups/${teamId}/applications`,
      update: (teamId, applicationId) => `/groups/${teamId}/applications/${applicationId}`,
    },
    // 团队提案
    proposals: {
      search: '/contents/search',  // 使用搜索API获取团队competitions
      create: (teamId) => `/groups/${teamId}/proposals`,
    },
  },

  // 通知相关
  notifications: {
    list: '/notifications',
    markRead: (id) => `/notifications/${id}/mark-read`,
    markAllRead: '/notifications/read-all',
    unreadCount: '/notifications/unread-count',
    delete: (id) => `/notifications/${id}`,
    // 邀请通知处理
    handleInvitation: (invitationId) => `/groups/invitations/${invitationId}/respond`,
    // 申请通知处理
    handleApplication: (teamId, applicationId) => `/groups/${teamId}/applications/${applicationId}`,
  },

  // OneAuth 认证端点（独立服务，不走 Synnovator 后端）
  // 注意：这些端点需要使用 VITE_KRATOS_PUBLIC_URL 或 VITE_ONEAUTH_URL 作为 base URL
  oneauth: {
    // Kratos Self-Service 端点
    kratos: {
      loginFlow: '/self-service/login/browser',
      loginSubmit: (flow) => `/self-service/login?flow=${flow}`,
      registrationFlow: '/self-service/registration/browser',
      registrationSubmit: (flow) => `/self-service/registration?flow=${flow}`,
      recoveryFlow: '/self-service/recovery/browser',
      verificationFlow: '/self-service/verification/browser',
      settingsFlow: '/self-service/settings/browser',
      session: '/sessions/whoami',
      logout: '/self-service/logout/browser',
    },
    // Native OAuth 端点（小程序用）
    native: {
      wechatLogin: '/oauth/native/wechat',
      session: '/oauth/native/session',
    },
    // Token 验证（业务后端使用）
    token: {
      verify: '/oauth/token/verify',
      introspect: '/oauth/token/introspect',
    },
    // OAuth2 相关 (Unified)
    oauth: {
      logout: '/api/oauth/logout',
      refresh: '/api/oauth/refresh',
    },
  },

  // 认证相关 - V2 API (Synnovator 后端，已废弃，保留用于兼容)
  auth: {
    oauth: {
      login: '/auth/oauth/login',  // 已废弃：应使用 oneauth.kratos
      callback: '/auth/oauth/callback',
      refresh: '/auth/oauth/refresh',
    },
    keycloak: {
      login: '/auth/keycloak/login',  // 已废弃：应使用 oneauth.kratos
      callback: '/auth/keycloak/callback',
      logout: '/auth/keycloak/logout',
    },
    wechat: {
      login: '/auth/wechat/login',
      verifyStudent: '/auth/wechat/verify-student',
    },
    me: '/auth/me',
    // logout: '/auth/logout', // 已废弃：应使用 oneauth.oauth.logout
    refresh: '/auth/refresh',
  },

  // 聊天相关
  chat: {
    messages: (userId) => `/chat/messages/${userId}`,
    send: '/chat/messages',
    users: '/chat/users',
  },

  // 菜单相关
  menu: {
    list: '/menu',
  },

  // 工作区相关
  workspace: {
    list: '/workspaces',
    current: '/workspaces/current',
    detail: (id) => `/workspaces/${id}`,
    switch: (id) => `/workspaces/${id}/switch`,
  },

  // 仪表盘相关
  dashboard: {
    overview: '/dashboard/overview',
    stats: '/dashboard/stats',
  },

  // 设置相关
  settings: {
    get: '/settings',
    update: '/settings',
  },

  // 筛选相关
  filter: {
    saveView: '/filter/views',
    savedViews: '/filter/views',
    viewDetail: (id) => `/filter/views/${id}`,
    deleteView: (id) => `/filter/views/${id}`,
  },

  // 附件相关 - 迁移到统一内容API
  attachments: {
    list: '/contents',  // ?type=image&type=video
    detail: (id) => `/contents/${id}`,
    upload: '/contents/file',  // 文件上传端点
    create: '/contents',  // 创建附件记录
    update: (id) => `/contents/${id}`,
    delete: (id) => `/contents/${id}`,
    search: '/contents/search',
    download: (id) => `/contents/${id}/file`,  // 下载文件
  },

  // 机器人对话相关 (API规范: /v1/rbt/chats)
  robotChat: {
    messages: (chatId) => `/rbt/chats/${chatId}/messages`,
  },

  // 模型相关 (API规范: /v1/syntrust/models)
  models: {
    list: '/models',
    detail: (id) => `/model/${id}`,
    evaluations: '/models/evaluations',
    compare: '/models/compare',
  },  

  // 模板相关
  template: {
    create: '/post',
  },

  // 提案相关 (API规范: /api/v2/contents)
  proposal: {
    create: '/contents',
    search: '/contents/search',
    detail: (id) => `/contents/${id}`,
  },

  // 参赛作品相关 - 迁移到统一内容API
  competition: {
    create: '/contents', // type=article&category=competition
    detail: (id) => `/contents/${id}`,
    update: (id) => `/contents/${id}`,
    delete: (id) => `/contents/${id}`,
    me: '/contents', // ?type=article&category=competition&user_id=current
    list: '/contents', // ?type=article&category=competition
    search: '/contents/search',
    // 评论和互动通过统一内容API
    comments: {
      list: (id) => `/contents/${id}/comments`,
      add: (id) => `/contents/${id}/comments`,
    },
    like: (id) => `/contents/${id}/like`,
  },

  // 找队友相关 - 迁移到统一内容API
  friends: {
    search: '/contents/search', // ?type=article&category=post&scope=recruit
    list: '/contents', // ?type=article&category=post&scope=recruit
  },

  // 招聘/找队友详情
  recruit: {
    detail: (id) => `/contents/${id}`,
    update: (id) => `/contents/${id}`,
    delete: (id) => `/contents/${id}`,
  },

  // 评论相关 - 迁移到统一内容API
  comment: {
    add: (contentId) => `/contents/${contentId}/comments`,
    list: (contentId) => `/contents/${contentId}/comments`,
    replies: (commentId) => `/contents/comments/${commentId}/replies`,
  },

  // 点赞相关 - 迁移到统一内容API
  like: {
    toggle: (contentId) => `/contents/${contentId}/like`,
  },

  // 收藏相关 - 迁移到统一内容API (如果后端支持)
  favorite: {
    toggle: (contentId) => `/contents/${contentId}/favorite`,
  },

  // 文档相关 (API规范: /v1/syntrust/documents)
  documents: {
    list: '/documents',
    detail: (id) => `/documents/${id}`,
    create: '/documents',
    update: (id) => `/documents/${id}`,
    delete: (id) => `/documents/${id}`,
    templates: '/documents/templates',
    search: '/documents/search',
  },
};

export default endpoints;

